import { GuardiansStackParamList } from "../types/guardiansNavigation"
import { webNavigate } from "@/navigation/webNavigate"
import { screenNameToPath } from "@/navigation/screenPaths"

export type GameNotificationNav = {
  screen: keyof GuardiansStackParamList
  params?: Record<string, unknown>
}

export type ResolvedGameNotificationLink =
  | { kind: "screen"; nav: GameNotificationNav }
  | { kind: "external"; url: string }
  | { kind: "none" }

function extractPath(link: string): string {
  let path = link.trim()
  try {
    if (/^https?:\/\//i.test(path)) {
      const url = new URL(path)
      path = url.pathname + url.search
    }
  } catch {
    // оставляем path как есть
  }
  return path.replace(/\/+$/, "")
}

/**
 * Маппинг link из уведомления («/game/...») → экран стека или внешний URL.
 */
export function resolveGameNotificationLink(
  link: string | null | undefined
): ResolvedGameNotificationLink {
  if (!link?.trim()) return { kind: "none" }

  const raw = link.trim()
  const path = extractPath(raw)
  const pathOnly = (path.split("?")[0] ?? path).toLowerCase()

  const routes: Array<{ match: RegExp; nav: GameNotificationNav }> = [
    { match: /^\/?game\/notifications$/, nav: { screen: "GameNotifications" } },
    { match: /^\/?game\/shop$/, nav: { screen: "Shop" } },
    { match: /^\/?game\/pet$/, nav: { screen: "Pet" } },
    { match: /^\/?game\/referral$/, nav: { screen: "Referral" } },
    { match: /^\/?game\/academy$/, nav: { screen: "Academy" } },
    {
      match: /^\/?game\/(daily-)?quests$/,
      nav: { screen: "DailyQuests" },
    },
    { match: /^\/?game\/leaderboard$/, nav: { screen: "Leaderboard" } },
    {
      match: /^\/?game\/battle-history$/,
      nav: { screen: "BattleHistory" },
    },
    {
      match: /^\/?game\/(city-)?chronicle$/,
      nav: { screen: "CityChronicle" },
    },
    {
      match: /^\/?game\/training(-gym)?$/,
      nav: { screen: "TrainingGym" },
    },
    {
      match: /^\/?game\/treasure$/,
      nav: {
        screen: "GuardiansTabs",
        params: { screen: "GuardiansMap" },
      },
    },
    {
      match: /^\/?game\/fuse$/,
      nav: {
        screen: "GuardiansTabs",
        params: { screen: "GuardiansCollection" },
      },
    },
    {
      match: /^\/?game\/virtual-tournament$/,
      nav: { screen: "VirtualTournament" },
    },
    {
      match: /^\/?game\/daily-virtual-tournament$/,
      nav: { screen: "DailyVirtualTournament" },
    },
    { match: /^\/?game\/arena(s)?$/, nav: { screen: "ArenaList" } },
    { match: /^\/?game\/boss(-map)?$/, nav: { screen: "BossMap" } },
    { match: /^\/?game\/world-boss$/, nav: { screen: "WorldBoss" } },
    { match: /^\/?game\/shards(-hub)?$/, nav: { screen: "ShardsHub" } },
    {
      match: /^\/?game\/(wardrobe|card-wardrobe)$/,
      nav: { screen: "CardWardrobe" },
    },
    { match: /^\/?game\/redeem$/, nav: { screen: "Redeem" } },
    {
      match: /^\/?game\/cards\/redeem$/,
      nav: { screen: "Redeem" },
    },
    {
      match:
        /^\/?game\/booster\/(coins|chakra|energy|shard-dust|style-token|fuse)\/redeem$/,
      nav: { screen: "Redeem" },
    },
    { match: /^\/?game\/help$/, nav: { screen: "Help" } },
    { match: /^\/?game\/war(-dashboard)?$/, nav: { screen: "WarDashboard" } },
    { match: /^\/?game\/rules$/, nav: { screen: "GameRules" } },
    {
      match: /^\/?game\/map$/,
      nav: {
        screen: "GuardiansTabs",
        params: { screen: "GuardiansMap" },
      },
    },
    {
      match: /^\/?game\/city-hall(\/raid)?$/,
      nav: { screen: "CityHall", params: {} },
    },
  ]

  const raidDetailMatch = pathOnly.match(/^\/?game\/city-hall\/raid\/([^/]+)$/)
  if (raidDetailMatch?.[1]) {
    return {
      kind: "screen",
      nav: {
        screen: "CityHallRaidBattle",
        params: { raidId: decodeURIComponent(raidDetailMatch[1]) },
      },
    }
  }

  for (const route of routes) {
    if (route.match.test(pathOnly)) {
      return { kind: "screen", nav: route.nav }
    }
  }

  if (/^https?:\/\//i.test(raw)) {
    return { kind: "external", url: raw }
  }

  // Относительный /game/... без известного экрана — открываем как веб-путь приложения
  if (pathOnly.startsWith("/game") || pathOnly.startsWith("game/")) {
    const normalized = path.startsWith("/") ? path : `/${path}`
    return {
      kind: "external",
      url: `https://app.daksby.by${normalized}`,
    }
  }

  return { kind: "none" }
}

export function openResolvedGameNotificationLink(
  resolved: ResolvedGameNotificationLink
): void {
  if (resolved.kind === "none") return
  if (resolved.kind === "external") {
    if (/^https?:\/\//i.test(resolved.url)) {
      window.open(resolved.url, "_blank", "noopener,noreferrer")
    } else {
      webNavigate(resolved.url)
    }
    return
  }
  webNavigate(screenNameToPath(resolved.nav.screen, resolved.nav.params))
}

export function formatGameNotificationLinkLabel(
  link: string | null | undefined
): string | null {
  if (!link?.trim()) return null
  const raw = link.trim()
  try {
    if (/^https?:\/\//i.test(raw)) {
      const url = new URL(raw)
      return url.pathname + url.search || raw
    }
  } catch {
    // ignore
  }
  return raw
}
